/**
 * Build Area Tool Schema
 * 
 * Schema for the tool that builds one area on a slide via the Python backend.
 * This tool is called by the ExecutionAgent for each area it decides to build.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// TARGET AREA (bounding box)
// ═══════════════════════════════════════════════════════════════════════════

export const TargetAreaSchema = z.object({
    left: z.number().describe("Left position in points"),
    top: z.number().describe("Top position in points"),
    width: z.number().describe("Width in points"),
    height: z.number().describe("Height in points"),
    right: z.number().optional().describe("Right position (calculated from left + width)"),
    bottom: z.number().optional().describe("Bottom position (calculated from top + height)"),
});

// ═══════════════════════════════════════════════════════════════════════════
// EXCEL ARTIFACT INSERTION (OLE or image from LinkedExcelEditor)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Insertion mode for Excel artifacts
 * - "ole": Editable Excel object (double-click to edit in Excel)
 * - "image": Static EMF picture (not editable)
 */
export const InsertionModeSchema = z.enum(["ole", "image"]).default("ole");

/**
 * Chart config with optional artifact reference
 * If artifactId is provided, the chart is inserted from an Excel artifact
 * instead of being created from scratch with chartType/data.
 */
export const ChartConfigSchema = z.object({
    // === ARTIFACT MODE (from LinkedExcelEditor) ===
    artifactId: z.string().optional().describe(
        "Excel artifact ID (artifact_xxx format). If provided, chart is inserted from mini-workbook instead of created from chartType/data."
    ),
    insertionMode: InsertionModeSchema.optional().describe(
        "How to insert the artifact: 'ole' (editable, default) or 'image' (static)"
    ),
    
    // === CREATE MODE (from scratch) ===
    chartType: z.string().optional().describe("Chart type (bar, line, pie, etc.) - ignored if artifactId provided"),
    // ... other chart config fields are passed through
}).passthrough();

/**
 * Table config with optional artifact reference
 * If artifactId is provided, the table is inserted from an Excel range artifact
 * instead of being created from scratch with rows/cellText.
 */
export const TableConfigSchema = z.object({
    // === ARTIFACT MODE (from LinkedExcelEditor) ===
    artifactId: z.string().optional().describe(
        "Excel artifact ID (artifact_xxx format). If provided, table is inserted from mini-workbook range instead of created from rows/cellText."
    ),
    insertionMode: InsertionModeSchema.optional().describe(
        "How to insert the artifact: 'ole' (editable, default) or 'image' (static)"
    ),
    
    // === CREATE MODE (from scratch) ===
    rows: z.number().optional().describe("Number of rows - ignored if artifactId provided"),
    columns: z.number().optional().describe("Number of columns - ignored if artifactId provided"),
    // ... other table config fields are passed through
}).passthrough();

// ═══════════════════════════════════════════════════════════════════════════
// COLLECTION ITEM (shape/text/chart to create)
// ═══════════════════════════════════════════════════════════════════════════

// This matches the Python buildArea schema - keeping it flexible with z.record
// The full schema is defined in Python's buildArea_schema.py
export const CollectionItemSchema = z.object({
    type: z.enum(["copy", "createShape", "createTable", "createChart", "createConnector"]),
    purpose: z.string().optional(),
    notes: z.string().optional(),
    position: z.object({
        left: z.number(),
        top: z.number(),
        width: z.number(),
        height: z.number(),
    }).optional(),
    rotation: z.number().optional(),
    zOrder: z.number().optional(),
    // Type-specific configs (use the schemas above for type checking)
    chartConfig: ChartConfigSchema.optional(),
    tableConfig: TableConfigSchema.optional(),
}).passthrough(); // Allow additional fields for type-specific configs

// ═══════════════════════════════════════════════════════════════════════════
// BUILD AREA PARAMS
// ═══════════════════════════════════════════════════════════════════════════

export const BuildAreaParamsSchema = z.object({
    slideIndex: z.number().describe("0-based slide index to build on"),
    areaDescription: z.string().describe("Human-readable description of what area is being built"),
    areaSpec: z.object({
        sectionId: z.string().optional().describe("Section ID (for logging)"),
        postChartMode: z.boolean().optional().describe("Whether in post-chart mode"),
        targetArea: TargetAreaSchema.optional().describe("Bounding box for the area"),
        collections: z.array(CollectionItemSchema).describe("Shapes/elements to create"),
    }).describe("The buildArea specification matching Python schema"),
});

export type BuildAreaParams = z.infer<typeof BuildAreaParamsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// BUILD AREA RESPONSE
// ═══════════════════════════════════════════════════════════════════════════

export const BuildAreaResponseSchema = z.object({
    success: z.boolean(),
    checkpointId: z.string().describe("5-digit checkpoint ID for rollback"),
    screenshotBase64: z.string().nullable().describe("Screenshot of the slide after build"),
    shapesCreated: z.array(z.record(z.string(), z.any())).describe("Info about created shapes"),
    shapeCount: z.number().describe("Number of shapes created"),
    errors: z.array(z.string()),
    warnings: z.array(z.string()).optional(),
});

export type BuildAreaResponse = z.infer<typeof BuildAreaResponseSchema>;


